import { useEffect, useMemo, useState } from "react"

import { App, Badge, Dropdown, Flex, Space, Tag, Tooltip, Typography } from "antd"
import dayjs from "dayjs"
import isEqual from "lodash.isequal"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useLocation } from "react-router-dom"

import ButtonComponent from "../../../Components/Button"
import { titleSortFilters } from "../../../Components/titleSortFilters"
import Icon from "../../../Icon"
import Images from "../../../Images"
import { alertsColumnIconMap, dateFormat, paymentProcessorMap, tagMap } from "../../../Utils/utils"
import {
  ACCESS_REQUEST_STATUSES,
  addCreditAutomationComment,
  exportCreditsAutomation,
  getCreditsAutomationList,
  getStatusOptions,
  updateAccessRequestStatus
} from "../redux/creditsAutomation.thunks"

import styles from "../styles.module.less"

const { Text } = Typography

export const PAGE_SIZE = 20

export const pinnedColumns = ["#", "Access Request status"]
export const exportItems = [
  { key: "all", label: "Export all" },
  { key: "as_filtered", label: "Export as filtered" }
]
export const noop = () => {}

const FOLDER_TO_STATUS = {
  pending: "Pending",
  action_needed: "Action needed",
  active: "Active",
  lost: "Lost",
  disabled: "Disabled"
}

const statusColor = (value) => {
  const lower = value?.toLowerCase()
  if (lower === "action needed") return "warning"
  if (lower === "disabled") return "default"
  return tagMap[lower] || "default"
}

// Pending and Disabled are set automatically (system); only these are admin-settable (CHA1-2908).
const MANUAL_STATUSES = ["Action needed", "Active", "Lost"]

const formatDate = (value) => (value ? dayjs(value).format(`${dateFormat} h:mm A`) : "—")

// `processor` is a display string, not a slug (see CREDIT_AUTOMATION_FRONTEND.md section 5) - it's
// sometimes the real display name ("Next Commerce") and sometimes a raw service_name fallback when
// the backend has none ("shopify", "stripe"). `paymentProcessorMap` already normalizes exactly
// those raw slugs to display names elsewhere in this app, so reuse it here too.
const formatProcessorName = (value) => paymentProcessorMap[value?.toLowerCase()] || value
const toAbsoluteUrl = (url) => (/^https?:\/\//i.test(url) ? url : `https://${url}`)

// Best-effort icon lookup from the display string, since this endpoint doesn't expose the raw
// service_name to key off directly - falls back to a generic icon when nothing matches.
const PROCESSOR_ICON_SLUGS = [
  "shopify_payments",
  "shopify",
  "stripe",
  "recurly",
  "braintree",
  "adyen",
  "airwallex",
  "klarna",
  "mollie",
  "paypal",
  "nmi",
  "chargebee"
]
const getProcessorIcon = (value) => {
  const normalized = value?.toLowerCase().replace(/\s+/g, "_") || ""
  const slug = PROCESSOR_ICON_SLUGS.find((candidate) => normalized.includes(candidate))
  return (
    Images[`integration_${slug === "shopify_payments" ? "shopify" : slug}`] ||
    Images.integration_missing
  )
}

// "Access Request status" is always shown (pinned), same as Credits' "request_status".
const defaultMyColumns = [
  { field_id: "access_request_date", label: "Access request date", active: true },
  { field_id: "access_update_date", label: "Access update date", active: true },
  { field_id: "processor", label: "Processor", active: true },
  { field_id: "business_account", label: "Business account", active: true },
  { field_id: "user_full_name", label: "User full name", active: true },
  { field_id: "user_email", label: "User email", active: false },
  { field_id: "alerts_amount", label: "Alerts amount", active: true },
  { field_id: "system_credits_amount", label: "System credits amount", active: true },
  { field_id: "action_needed_reason", label: "Action needed reason", active: true },
  { field_id: "comments", label: "Comments", active: true },
  { field_id: "merchant_confirmed_at", label: "Merchant confirmed", active: false },
  { field_id: "last_access_check_at", label: "Last access check", active: false }
]

export const allFilters = [
  { field_id: "access_request_status", label: "Access Request status" },
  { field_id: "business_account", label: "Business account" },
  { field_id: "processor", label: "Processor" },
  { field_id: "access_request_date", label: "Access request date" },
  { field_id: "access_update_date", label: "Access update date" },
  { field_id: "user_full_name", label: "User full name" },
  { field_id: "user_email", label: "User email" },
  { field_id: "action_needed_reason", label: "Action needed reason" }
]

export const mockFilterOptions = {
  access_request_status: ACCESS_REQUEST_STATUSES
}

const DEFAULT_FILTERS = {
  access_request_status: "all",
  processor: [],
  business_account: [],
  access_request_date: null,
  access_update_date: null,
  user_full_name: [],
  user_email: [],
  action_needed_reason: "all"
}

export const useCreditsAutomationPage = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { notification } = App.useApp()
  const { search } = useLocation()
  const isMobile = useSelector((state) => state.nav.isMobile)
  const {
    results,
    count,
    fetching,
    updateStatusFetching,
    addCommentFetching,
    exportFetching,
    reasonOptions
  } = useSelector((state) => state.creditsAutomation)

  const folder = new URLSearchParams(search).get("folder")

  const [pageNumber, setPageNumber] = useState(1)
  const [ordering, setOrdering] = useState(null)
  const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const [visibleFilters, setVisibleFilters] = useState({
    access_request_status: true,
    business_account: true,
    processor: true
  })
  const [myColumns, setMyColumns] = useState(defaultMyColumns)
  const [changeStatusRecord, setChangeStatusRecord] = useState(null)
  const [commentRecord, setCommentRecord] = useState(null)

  useEffect(() => {
    dispatch(getStatusOptions())
  }, [dispatch])

  useEffect(() => {
    setPageNumber(1)
  }, [folder])

  useEffect(() => {
    const folderStatus = FOLDER_TO_STATUS[folder]
    dispatch(
      getCreditsAutomationList({
        page: pageNumber,
        page_size: PAGE_SIZE,
        ordering,
        filters: {
          ...filters,
          access_request_status: folderStatus || filters.access_request_status
        }
      })
    )
  }, [dispatch, pageNumber, ordering, folder, filters])

  const handleChangeFilter = (newData, type) => {
    if (type === "changeVisible") {
      setVisibleFilters((prev) => ({ ...prev, ...newData }))
      return
    }
    setFilters((prev) => ({ ...prev, ...newData }))
    setPageNumber(1)
  }

  const isFiltersDefault = isEqual(filters, DEFAULT_FILTERS)

  const handleClearFilters = () => {
    setFilters(DEFAULT_FILTERS)
    setPageNumber(1)
  }

  const handleExport = async (key) => {
    const export_type = key === "as_filtered" ? "as_filtered" : "all"
    const folderStatus = FOLDER_TO_STATUS[folder]
    const result = await dispatch(
      exportCreditsAutomation({
        export_type,
        filters: {
          ...filters,
          access_request_status: folderStatus || filters.access_request_status
        }
      })
    )
    const notificationConfig = { closeIcon: <Text>{t("Dismiss")}</Text> }
    if (exportCreditsAutomation.fulfilled.match(result)) {
      notification.success({
        message: t("Exporting"),
        description: t("Users will be downloaded shortly."),
        ...notificationConfig
      })
    } else {
      const data = result.payload
      const message =
        (data && typeof data === "object" ? Object.values(data)[0] : data) || t("Export failed.")
      notification.error({ message, ...notificationConfig })
    }
  }

  const filterOptions = useMemo(
    () => ({
      ...mockFilterOptions,
      action_needed_reason: (reasonOptions || []).map((option) => ({
        name: option?.label ?? option?.name ?? option,
        value: option?.value ?? option
      }))
    }),
    [reasonOptions]
  )

  const handleChangeStatus = (record, status) => {
    if (status === "Action needed") {
      setChangeStatusRecord(record)
      return
    }
    dispatch(updateAccessRequestStatus({ id: record.id, status }))
  }

  const handleCloseStatusModal = () => setChangeStatusRecord(null)

  const handleApproveStatusChange = ({ reason, details }) => {
    dispatch(
      updateAccessRequestStatus({
        id: changeStatusRecord.id,
        status: "Action needed",
        failure_reason: reason,
        failure_details: details
      })
    )
    setChangeStatusRecord(null)
  }

  const handleOpenCommentModal = (record) => setCommentRecord(record)
  const handleCloseCommentModal = () => setCommentRecord(null)

  const handleSaveComment = async (comment) => {
    const id = commentRecord?.id
    if (!id) return
    const result = await dispatch(addCreditAutomationComment({ id, comment }))
    // Keep the pop-up open on failure (the global error toast already explains why).
    if (addCreditAutomationComment.fulfilled.match(result)) {
      setCommentRecord(null)
    }
  }

  const columns = useMemo(
    () => [
      {
        title: "#",
        key: "sequence_number",
        width: 64,
        fixed: "left",
        render: (_, __, index) => (pageNumber - 1) * PAGE_SIZE + index + 1
      },
      {
        title: (state) =>
          titleSortFilters({
            title: t("Access Request status"),
            sorter: true,
            stateData: state,
            icon: alertsColumnIconMap["access_request_status"],
            key: "access_request_status"
          }),
        dataIndex: "access_request_status",
        key: "access_request_status",
        width: 190,
        fixed: "left",
        sorter: true,
        // CHA1-3315: `has_integration_issue`/`integration_issue_reason` come on the same row as
        // the status - icon sits next to the status tag, same tooltip-trigger pattern as
        // AlertStatusTag (src/Containers/AlertsPage/components/AlertStatusTag/AlertStatusTag.js).
        render: (value, record) => (
          <Flex align="center">
            {record.is_managed_by_app ? (
              // CHA1-3566: our app holds the access for this processor (Stripe) and the status
              // follows the token, so it's read-only here - plain badge, no change-status dropdown.
              <Tag bordered={false} color={statusColor(value)} className="large">
                <Flex gap={8} align="center">
                  <Badge status={statusColor(value)} />
                  <Text>{t(value)}</Text>
                </Flex>
              </Tag>
            ) : (
              <Dropdown
                menu={{
                  items: MANUAL_STATUSES.filter((status) => status !== value).map((status) => ({
                    key: status,
                    label: (
                      <Flex gap={8} align="center">
                        <Badge status={statusColor(status)} />
                        <Text>{t(status)}</Text>
                      </Flex>
                    )
                  })),
                  onClick: ({ key, domEvent }) => {
                    domEvent.stopPropagation()
                    handleChangeStatus(record, key)
                  }
                }}
              >
                <Tag bordered={false} color={statusColor(value)} className="large">
                  <Flex gap={8} align="center">
                    <Badge status={statusColor(value)} />
                    <Text>{t(value)}</Text>
                    <Icon type="chevron_down_small" style={{ marginLeft: -4 }} />
                  </Flex>
                </Tag>
              </Dropdown>
            )}
            {Boolean(record.has_integration_issue) && (
              <Tooltip title={record.integration_issue_reason} destroyTooltipOnHide={true}>
                <div style={{ height: 32, width: 32 }} className={styles.tooltipTrigger}>
                  <Icon type="triangle_exclamation2" />
                </div>
              </Tooltip>
            )}
          </Flex>
        )
      },
      {
        title: (state) =>
          titleSortFilters({
            title: t("Access request date"),
            sorter: true,
            stateData: state,
            icon: alertsColumnIconMap["access_request_date"],
            key: "access_request_date"
          }),
        dataIndex: "access_request_date",
        key: "access_request_date",
        width: 180,
        sorter: true,
        render: formatDate
      },
      {
        title: (state) =>
          titleSortFilters({
            title: t("Access update date"),
            sorter: true,
            stateData: state,
            icon: alertsColumnIconMap["access_update_date"],
            key: "access_update_date"
          }),
        dataIndex: "access_update_date",
        key: "access_update_date",
        width: 180,
        sorter: true,
        render: formatDate
      },
      {
        title: (state) =>
          titleSortFilters({
            title: t("Processor"),
            sorter: true,
            stateData: state,
            icon: alertsColumnIconMap["processor"],
            key: "processor"
          }),
        dataIndex: "processor",
        key: "processor",
        width: 220,
        sorter: true,
        // CHA1-2908: "Processor - with a redirect option to log into the PP account" - the name
        // itself is plain text (just identifies the integration); only the external-link glyph is
        // the actual redirect, and only when the backend sent a link for that row.
        render: (value, record) => (
          <Space>
            <img src={getProcessorIcon(value)} width={20} height={20} alt={value} />
            <Text>{formatProcessorName(value)}</Text>
            {record.processor_link && (
              <a href={toAbsoluteUrl(record.processor_link)} target="_blank" rel="noreferrer">
                <Icon type="open_link" style={{ fontSize: 14 }} />
              </a>
            )}
          </Space>
        )
      },
      {
        title: (state) =>
          titleSortFilters({
            title: t("Business account"),
            sorter: true,
            stateData: state,
            icon: alertsColumnIconMap["business_account"],
            key: "business_account"
          }),
        dataIndex: "business_account",
        key: "business_account",
        width: 190,
        sorter: true,
        ellipsis: true,
        render: (value) => (
          <Space>
            <Icon type="business" style={{ fontSize: 16 }} />
            <Text ellipsis>{value}</Text>
          </Space>
        )
      },
      {
        title: (state) =>
          titleSortFilters({
            title: t("User full name"),
            sorter: true,
            stateData: state,
            icon: alertsColumnIconMap["user_full_name"],
            key: "user_full_name"
          }),
        dataIndex: "user_full_name",
        key: "user_full_name",
        width: 190,
        sorter: true,
        ellipsis: true
      },
      {
        title: (state) =>
          titleSortFilters({
            title: t("User email"),
            sorter: true,
            stateData: state,
            icon: alertsColumnIconMap["user_email"],
            key: "user_email"
          }),
        dataIndex: "user_email",
        key: "user_email",
        width: 190,
        sorter: true,
        ellipsis: true
      },
      {
        // Not in the documented ordering whitelist yet (CREDIT_AUTOMATION_FRONTEND.md section 5.3) -
        // no `sorter` until that's confirmed, since an unrecognized `ordering` value 500s.
        title: (state) => titleSortFilters({ title: t("Alerts amount"), icon: alertsColumnIconMap["alerts_amount"], stateData: state }),
        dataIndex: "alerts_amount",
        key: "alerts_amount",
        width: 150,
        render: (value) => value?.toLocaleString() ?? "—"
      },
      {
        title: (state) => titleSortFilters({ title: t("System credits amount"), icon: alertsColumnIconMap["system_credits_amount"], stateData: state }),
        dataIndex: "system_credits_amount",
        key: "system_credits_amount",
        width: 180,
        render: (value) => value?.toLocaleString() ?? "—"
      },
      {
        title: (state) =>
          titleSortFilters({
            title: t("Action needed reason"),
            sorter: true,
            stateData: state,
            icon: alertsColumnIconMap["action_needed_reason"],
            key: "action_needed_reason"
          }),
        dataIndex: "action_needed_reason",
        key: "action_needed_reason",
        width: 220,
        sorter: true,
        render: (value) => (value ? t(value) : "—")
      },
      {
        title: (state) =>
          titleSortFilters({
            title: t("Comments"),
            stateData: state,
            icon: alertsColumnIconMap["comments"],
            key: "comments"
          }),
        dataIndex: "comments",
        key: "comments",
        width: 260,
        ellipsis: true,
        render: (value) =>
          value ? (
            <Tooltip title={value}>
              <Text ellipsis>{value}</Text>
            </Tooltip>
          ) : (
            "—"
          )
      },
      {
        title: (state) =>
          titleSortFilters({
            title: t("Merchant confirmed"),
            stateData: state,
            icon: alertsColumnIconMap["merchant_confirmed_at"],
            key: "merchant_confirmed_at"
          }),
        dataIndex: "merchant_confirmed_at",
        key: "merchant_confirmed_at",
        width: 180,
        render: formatDate
      },
      {
        title: (state) =>
          titleSortFilters({
            title: t("Last access check"),
            stateData: state,
            icon: alertsColumnIconMap["last_access_check_at"],
            key: "last_access_check_at"
          }),
        dataIndex: "last_access_check_at",
        key: "last_access_check_at",
        width: 180,
        render: formatDate
      },
      {
        // CHA1-2908: "Actions: icons" -> "Add comments" -> "confirmation pop-up opens".
        // CHA1-3068: the "Add comment" action persists via PATCH …/<id>/comment/ (addCreditAutomationComment).
        title: "",
        key: "actions",
        width: 56,
        fixed: "right",
        render: (_, record) => (
          <ButtonComponent
            type="text"
            shape="circle"
            icon={<Icon type="comment" style={{ fontSize: 18 }} />}
            onClick={() => handleOpenCommentModal(record)}
          />
        )
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, pageNumber]
  )

  const tableColumns = useMemo(() => {
    const byKey = Object.fromEntries(columns.map((column) => [column.key, column]))
    const pinnedStart = ["sequence_number", "access_request_status"]
      .map((key) => byKey[key])
      .filter(Boolean)
    const ordered = myColumns
      .filter(({ active }) => active)
      .map(({ field_id }) => byKey[field_id])
      .filter(Boolean)
    return [...pinnedStart, ...ordered, byKey["actions"]].filter(Boolean)
  }, [columns, myColumns])

  return {
    isMobile,
    results,
    count,
    fetching,

    filters,
    visibleFilters,
    handleChangeFilter,
    handleClearFilters,
    isFiltersDefault,
    filterOptions,
    handleExport,
    exportFetching,

    myColumns,
    setMyColumns,
    tableColumns,

    pageNumber,
    setPageNumber,
    setOrdering,

    changeStatusRecord,
    handleCloseStatusModal,
    handleApproveStatusChange,
    reasonOptions,
    updateStatusFetching,

    commentRecord,
    handleCloseCommentModal,
    handleSaveComment,
    addCommentFetching
  }
}
